import { samePagePath } from "./authReturn";
import { tenantRoute } from "./tenantRoute";

/** ai_engine's refusal codes for a project URL (company-session and every API call in the tab). */
export const REFUSAL = {
    disabled: "project_route_disabled",
    notMember: "project_membership_required",
    notFound: "project_not_found",
    companyNotFound: "company_not_found",
    authRequired: "company_authentication_required",
    inactive: "project_inactive",
} as const;

const KNOWN_CODES = Object.values(REFUSAL);

export interface RefusalInfo {
    status: number | null;
    code: string | null;
    retryAfterMs: number | null;
    /** On company-session's disabled route: whether the unprefixed path admits through the
     *  browser session's own organization. Null from a server that does not say. */
    legacyPath: boolean | null;
    /** On a step-up: the Stytch sign-in methods the project's organization accepts, when named. */
    allowedAuthMethods: string[] | null;
}

/** Read status, refusal code and Retry-After off a failed request, whatever the detail's shape. */
export function refusalInfo(failure: unknown): RefusalInfo {
    const response = (
        failure as {
            response?: { status?: number; data?: { detail?: unknown }; headers?: unknown };
        }
    )?.response;
    const detail = response?.data?.detail;
    const text = typeof detail === "string" ? detail : (JSON.stringify(detail) ?? "");
    const code = KNOWN_CODES.find((known) => text.includes(known)) ?? null;
    const fields =
        detail && typeof detail === "object"
            ? (detail as { legacy_path?: unknown; allowed_auth_methods?: unknown })
            : {};
    const methods = Array.isArray(fields.allowed_auth_methods)
        ? fields.allowed_auth_methods.filter(
              (method): method is string => typeof method === "string",
          )
        : [];
    const headers = response?.headers as
        { get?: (name: string) => unknown; [key: string]: unknown } | undefined;
    const raw =
        typeof headers?.get === "function" ? headers.get("retry-after") : headers?.["retry-after"];
    const seconds = typeof raw === "string" || typeof raw === "number" ? Number(raw) : Number.NaN;
    return {
        status: typeof response?.status === "number" ? response.status : null,
        code,
        retryAfterMs: Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null,
        legacyPath: typeof fields.legacy_path === "boolean" ? fields.legacy_path : null,
        allowedAuthMethods: methods.length ? methods : null,
    };
}

export function isRouteDisabled(failure: unknown): boolean {
    const { status, code } = refusalInfo(failure);
    return status === 403 && code === REFUSAL.disabled;
}

const FALLBACK_KEY = "pp.catalogFallback";
const FALLBACK_TTL_MS = 2 * 60_000;

function storage(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/** The same page without its `/{company}/{project}` prefix, or null when it has none or what is
 *  left is not a same-origin path. The URL only selects the project, so a suffix such as
 *  `//host/x` or `/\host` would otherwise become a navigation off this origin. Checked as a
 *  path, never against the page's scheme, so the iOS bundle (`capacitor://localhost`) keeps it. */
export function unprefixedUrl(): string | null {
    const route = tenantRoute();
    if (!route.companySlug || !route.projectSlug) return null;
    return samePagePath(route.pathname + window.location.search + window.location.hash);
}

/** The `/{company}/{project}` URL prefix a fallback leaves, or null outside a project URL. */
function projectPrefix(): string | null {
    const { companySlug, projectSlug } = tenantRoute();
    return companySlug && projectSlug ? `/${companySlug}/${projectSlug}`.toLowerCase() : null;
}

/** The project prefix this tab fell back from a moment ago, or null. */
function recentFallback(): string | null {
    const [stamp, marked] = (storage()?.getItem(FALLBACK_KEY) ?? "").split("|");
    const at = Number(stamp);
    return Number.isFinite(at) && at > 0 && Date.now() - at < FALLBACK_TTL_MS && marked
        ? marked
        : null;
}

/** True when this project's URL fell back a moment ago; another project's fallback is unaffected. */
function cameFromFallback(prefix: string): boolean {
    return recentFallback() === prefix;
}

/** True when this tab just fell back from any switched-off project URL to its unprefixed path:
 *  that path is the server's answer, so a remembered project must not replace it. */
export function fellBackFromProjectUrl(): boolean {
    return recentFallback() !== null;
}

export function clearFallbackMarker(): void {
    storage()?.removeItem(FALLBACK_KEY);
}

/** A disabled project URL falls back to the legacy path, once per project, unless company-session
 *  said that path admits through another organization (`legacy_path: false`). Returns whether it
 *  left. A server that does not say keeps the fallback. */
export function redirectToUnprefixedRoute(failure: unknown): boolean {
    if (refusalInfo(failure).legacyPath === false) return false;
    const target = unprefixedUrl();
    const prefix = projectPrefix();
    if (!target || !prefix || cameFromFallback(prefix)) return false;
    storage()?.setItem(FALLBACK_KEY, `${Date.now()}|${prefix}`);
    window.location.replace(target);
    return true;
}

const authKey = (path: string) => `pp.companyAuthTried:${path}`;

/** Recorded when the person follows "Sign in to company" for this path. */
export function markCompanySignInTried(path = window.location.pathname): void {
    storage()?.setItem(authKey(path), "1");
}

export function companySignInWasTried(path = window.location.pathname): boolean {
    return storage()?.getItem(authKey(path)) === "1";
}

export function clearCompanySignInMarker(path = window.location.pathname): void {
    storage()?.removeItem(authKey(path));
}

const reloadKey = (path: string) => `pp.refusalReloaded:${path}`;

/** A renewal refusal closed this Document's project session, and the refusal screen's chooser
 *  cannot load inside it. Reload once into a fresh Document, where initial admission renders the
 *  refusal. The marker (per path, cleared when admission succeeds) keeps it from looping. */
export function reloadForRefusal(path = window.location.pathname): boolean {
    const store = storage();
    if (!store || store.getItem(reloadKey(path)) === "1") return false;
    store.setItem(reloadKey(path), "1");
    window.location.reload();
    return true;
}

export function clearRefusalReloadMarker(path = window.location.pathname): void {
    storage()?.removeItem(reloadKey(path));
}
